import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Share2, Twitter, Facebook, MessageCircle, Link2, Check, Download, ChevronDown, ExternalLink } from 'lucide-react';

// Global tracker for currently open share dropdown
let currentOpenDropdown = null;
const closeCurrentDropdown = () => {
  if (currentOpenDropdown) {
    currentOpenDropdown();
    currentOpenDropdown = null;
  }
};

export const ShareButtons = ({ 
  type, 
  id, 
  title, 
  videoUrl = null,  // For direct MP4 download
  onCreateVideo = null,  // Callback when user needs to create video first
  compact = false  // For smaller button in card hovers
}) => {
  const [copied, setCopied] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showPostInstructions, setShowPostInstructions] = useState(false);
  const buttonRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Close this dropdown and update global tracker
  const closeDropdown = () => {
    setShowButtons(false);
    setShowPostInstructions(false);
  };

  // Handle opening - close any other open dropdown first
  const handleToggle = () => {
    if (showButtons) {
      closeDropdown();
      currentOpenDropdown = null;
    } else {
      closeCurrentDropdown(); // Close any other open dropdown
      setShowButtons(true);
      currentOpenDropdown = closeDropdown;
    }
  };

  // Update dropdown position when showing in compact mode
  useEffect(() => {
    if (showButtons && compact && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: Math.max(8, rect.left - 200 + rect.width) // Align right edge, but keep on screen
      });
    }
  }, [showButtons, compact]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentOpenDropdown === closeDropdown) {
        currentOpenDropdown = null;
      }
    };
  }, []);

  // Normalize video types to use 'video' route (slideshow, cover, birthday all use /video/:id)
  const urlType = (type === 'slideshow' || type === 'cover' || type === 'birthday_video') ? 'video' : type;
  
  // Direct link for copying/sharing - use public player for shareable content
  const isShareable = ['gift', 'song', 'video', 'slideshow', 'cover', 'birthday_video'].includes(type);
  const directUrl = isShareable 
    ? `${window.location.origin}/play/${urlType}/${id}`
    : `${window.location.origin}/${urlType}/${id}`;
  
  // Social share URL with OG tags (for Facebook, Twitter previews)
  // Use the API endpoint that returns HTML with OG tags, which redirects to the player
  const socialShareUrl = (type === 'gift' || type === 'song' || type === 'birthday')
    ? `${window.location.origin}/api/share/${type}/${id}`
    : directUrl;
  
  const shareText = type === 'birthday' 
    ? `🎂 Check out this birthday song for ${title}! Created with AI on Wrapped In Music! 🎵`
    : `Check out "${title}" - created with AI on Wrapped In Music! 🎵`;

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(socialShareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(socialShareUrl)}&quote=${encodeURIComponent(shareText)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + directUrl)}`
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(directUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleShare = (platform) => {
    window.open(shareLinks[platform], '_blank', 'width=600,height=400');
  };

  const handleDownloadMP4 = async () => {
    // Determine download URL - prefer direct video URL if available
    let downloadUrl = videoUrl;

    if (!downloadUrl && id && type === 'video') {
      // We only have a raw ID and it IS a video_id — use the backend
      // proxy. NEVER use this branch for `type === 'song'` because
      // `song_id` won't resolve inside the videos collection (that's
      // how we get 404s like `/api/videos/song_xxx/download`).
      downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/videos/${id}/download`;
    }

    if (!downloadUrl) {
      // No video exists yet — trigger the "make a video" flow so the
      // user has something to download / share.
      if (onCreateVideo) {
        onCreateVideo();
        setShowButtons(false);
      } else {
        toast.error('No video available. Create a video first!');
      }
      return;
    }

    // If we have a direct URL (HP server or other), use simple link download
    if (videoUrl && !videoUrl.includes('/api/')) {
      const a = document.createElement('a');
      a.href = videoUrl;
      a.download = `${title?.replace(/[^a-z0-9]/gi, '_') || 'video'}.mp4`;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Download started!');
      setShowPostInstructions(true);
      return;
    }

    // Download through backend proxy
    setDownloading(true);
    try {
      const response = await fetch(downloadUrl, { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Download failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title?.replace(/[^a-z0-9]/gi, '_') || 'video'}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Download started!');
      // Show post instructions after download
      setShowPostInstructions(true);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download video');
    } finally {
      setDownloading(false);
    }
  };

  /**
   * Direct MP3 download for song-type shares. Uses the anchor-tag
   * pattern (rather than fetch+blob) so cookies flow same-origin,
   * matching the standalone Download button on SongDetail.
   */
  const handleDownloadMP3 = () => {
    if (!id) {
      toast.error('No song to download.');
      return;
    }
    const a = document.createElement('a');
    a.href = `${process.env.REACT_APP_BACKEND_URL}/api/songs/${id}/download-with-cover`;
    a.download = `${title?.replace(/[^a-z0-9]/gi, '_') || 'song'}.mp3`;
    a.target = '_self';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Download started!');
    setShowButtons(false);
  };

  const openFacebookComposer = () => {
    window.open('https://www.facebook.com/', '_blank');
  };

  return (
    <div className="relative" ref={buttonRef}>
      <Button
        onClick={handleToggle}
        className={compact 
          ? "px-2 py-1 bg-purple-500/80 hover:bg-purple-500 text-white rounded text-[10px] font-medium transition-colors flex items-center gap-1 h-auto"
          : "bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-full flex items-center gap-2"
        }
        data-testid="share-toggle-btn"
      >
        <Share2 className={compact ? "h-3 w-3" : "h-4 w-4"} />
        <span>Share</span>
        {!compact && <ChevronDown className={`h-3 w-3 transition-transform ${showButtons ? 'rotate-180' : ''}`} />}
      </Button>

      {showButtons && (
        <>
          {/* Backdrop to close dropdown */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => {
              closeDropdown();
              currentOpenDropdown = null;
            }}
          />
          
          {/* Dropdown Menu - use portal for compact mode to escape overflow:hidden */}
          {compact ? createPortal(
            <div 
              className="fixed z-[100] glass-card p-2 min-w-[240px] shadow-xl border border-white/10"
              style={{ 
                top: dropdownPosition.top,
                left: dropdownPosition.left
              }}
              data-testid="share-buttons"
            >
              {/* Post Instructions after download */}
              {showPostInstructions && (
                <>
                  <div className="px-3 py-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-2">
                    <p className="text-green-400 text-sm font-medium mb-2">✓ Video Downloaded!</p>
                    <p className="text-white/70 text-xs mb-3">
                      To share on Facebook or TikTok:
                    </p>
                    <ol className="text-white/60 text-xs space-y-1 list-decimal list-inside mb-3">
                      <li>Open Facebook/TikTok</li>
                      <li>Create a new post</li>
                      <li>Upload the downloaded video</li>
                    </ol>
                    <button
                      onClick={openFacebookComposer}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#4267B2] hover:bg-[#365899] transition-colors text-white text-sm"
                    >
                      <Facebook className="h-4 w-4" />
                      <span>Open Facebook</span>
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="border-t border-white/10 my-1" />
                </>
              )}

              {/* Copy Link */}
              <button
                onClick={handleCopyLink}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-left"
                data-testid="share-copy-btn"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Link2 className="h-4 w-4 text-zinc-400" />
                )}
                <span className="text-white text-sm">Copy Link</span>
              </button>

              {/* Divider */}
              <div className="border-t border-white/10 my-1" />

              {/* Facebook */}
              <button
                onClick={() => handleShare('facebook')}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-left"
                data-testid="share-facebook-btn"
              >
                <Facebook className="h-4 w-4 text-[#4267B2]" />
                <span className="text-white text-sm">Share Link on Facebook</span>
              </button>

              {/* Twitter/X */}
              <button
                onClick={() => handleShare('twitter')}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-left"
                data-testid="share-twitter-btn"
              >
                <Twitter className="h-4 w-4 text-[#1DA1F2]" />
                <span className="text-white text-sm">Share on X (Twitter)</span>
              </button>

              {/* WhatsApp */}
              <button
                onClick={() => handleShare('whatsapp')}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-left"
                data-testid="share-whatsapp-btn"
              >
                <MessageCircle className="h-4 w-4 text-[#25D366]" />
                <span className="text-white text-sm">Share on WhatsApp</span>
              </button>

              {/* Divider */}
              <div className="border-t border-white/10 my-1" />

              {/* Download MP3 — only for song-type shares */}
              {type === 'song' && id && (
                <button
                  onClick={handleDownloadMP3}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-left"
                  data-testid="share-download-mp3-btn"
                >
                  <Download className="h-4 w-4 text-emerald-400" />
                  <div className="flex flex-col">
                    <span className="text-white text-sm">Download MP3</span>
                    <span className="text-zinc-500 text-xs">Audio only, with embedded cover art</span>
                  </div>
                </button>
              )}

              {/* Download MP4 */}
              <button
                onClick={handleDownloadMP4}
                disabled={downloading}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-left disabled:opacity-50"
                data-testid="share-download-mp4-btn"
              >
                <Download className={`h-4 w-4 text-purple-400 ${downloading ? 'animate-pulse' : ''}`} />
                <div className="flex flex-col">
                  <span className="text-white text-sm">
                    {downloading ? 'Downloading...' : 'Download MP4'}
                  </span>
                  <span className="text-zinc-500 text-xs">For posting videos on Facebook/TikTok</span>
                </div>
              </button>
              
              {!videoUrl && !id && (
                <p className="text-xs text-zinc-500 px-3 py-1">
                  No video yet — click to create one
                </p>
              )}
            </div>,
            document.body
          ) : (
            <div 
              className="absolute right-0 top-full mt-2 glass-card p-2 z-50 min-w-[240px] shadow-xl border border-white/10"
              data-testid="share-buttons"
            >
              {/* Post Instructions after download */}
              {showPostInstructions && (
                <>
                  <div className="px-3 py-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-2">
                    <p className="text-green-400 text-sm font-medium mb-2">✓ Video Downloaded!</p>
                    <p className="text-white/70 text-xs mb-3">
                      To share on Facebook or TikTok:
                    </p>
                    <ol className="text-white/60 text-xs space-y-1 list-decimal list-inside mb-3">
                      <li>Open Facebook/TikTok</li>
                      <li>Create a new post</li>
                      <li>Upload the downloaded video</li>
                    </ol>
                    <button
                      onClick={openFacebookComposer}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#4267B2] hover:bg-[#365899] transition-colors text-white text-sm"
                    >
                      <Facebook className="h-4 w-4" />
                      <span>Open Facebook</span>
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="border-t border-white/10 my-1" />
                </>
              )}

              {/* Copy Link */}
              <button
                onClick={handleCopyLink}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-left"
                data-testid="share-copy-btn"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Link2 className="h-4 w-4 text-zinc-400" />
                )}
                <span className="text-white text-sm">Copy Link</span>
              </button>

              {/* Divider */}
              <div className="border-t border-white/10 my-1" />

              {/* Facebook */}
              <button
                onClick={() => handleShare('facebook')}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-left"
                data-testid="share-facebook-btn"
              >
                <Facebook className="h-4 w-4 text-[#4267B2]" />
                <span className="text-white text-sm">Share Link on Facebook</span>
              </button>

              {/* Twitter/X */}
              <button
                onClick={() => handleShare('twitter')}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-left"
                data-testid="share-twitter-btn"
              >
                <Twitter className="h-4 w-4 text-[#1DA1F2]" />
                <span className="text-white text-sm">Share on X (Twitter)</span>
              </button>

              {/* WhatsApp */}
              <button
                onClick={() => handleShare('whatsapp')}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-left"
                data-testid="share-whatsapp-btn"
              >
                <MessageCircle className="h-4 w-4 text-[#25D366]" />
                <span className="text-white text-sm">Share on WhatsApp</span>
              </button>

              {/* Divider */}
              <div className="border-t border-white/10 my-1" />

              {/* Download MP3 — only for song-type shares */}
              {type === 'song' && id && (
                <button
                  onClick={handleDownloadMP3}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-left"
                  data-testid="share-download-mp3-btn-inline"
                >
                  <Download className="h-4 w-4 text-emerald-400" />
                  <div className="flex flex-col">
                    <span className="text-white text-sm">Download MP3</span>
                    <span className="text-zinc-500 text-xs">Audio only, with embedded cover art</span>
                  </div>
                </button>
              )}

              {/* Download MP4 */}
              <button
                onClick={handleDownloadMP4}
                disabled={downloading}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-left disabled:opacity-50"
                data-testid="share-download-mp4-btn"
              >
                <Download className={`h-4 w-4 text-purple-400 ${downloading ? 'animate-pulse' : ''}`} />
                <div className="flex flex-col">
                  <span className="text-white text-sm">
                    {downloading ? 'Downloading...' : 'Download MP4'}
                  </span>
                  <span className="text-zinc-500 text-xs">For posting videos on Facebook/TikTok</span>
                </div>
              </button>
              
              {!videoUrl && !id && (
                <p className="text-xs text-zinc-500 px-3 py-1">
                  No video yet — click to create one
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShareButtons;
